import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId, type TestIdProps } from '../utils';

const heroSectionVariants = cva(
  'flex flex-col items-center justify-center text-center text-foreground-base',
  {
    variants: {
      size: {
        sm: 'gap-sm px-md py-lg',
        md: 'gap-md px-md py-xl',
        lg: 'gap-lg px-md py-2xl'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

type HeroSectionVariantProps = VariantProps<typeof heroSectionVariants>;

type BaseHeroSectionAttributes = Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

export interface HeroSectionProps
  extends BaseHeroSectionAttributes, HeroSectionVariantProps, TestIdProps {
  /** Illustration or image shown above the title (e.g. a mascot Avatar) */
  picture?: React.ReactNode;
  /** Primary heading text */
  title: string;
  /** Optional supporting message under the title */
  message?: string;
  /** Optional action element (e.g. a Button) */
  action?: React.ReactNode;
  id?: string;
}

/**
 * HeroSection component
 *
 * Centered hero surface with an optional picture, a title, and a supporting
 * message — for all-clear/celebratory states and content-free views.
 *
 * @example
 * ```tsx
 * <HeroSection picture={<Avatar mascot='sunglasses' size='xl' />} title={t('allOk')} />
 * ```
 */
export const HeroSection = React.forwardRef<HTMLDivElement, HeroSectionProps>(
  ({ picture, title, message, action, size, className, testId, id, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-testid={getTestId({ testId, id })}
        className={cn(heroSectionVariants({ size }), className)}
        {...props}
      >
        {picture && (
          <div aria-hidden='true' className='flex items-center justify-center'>
            {picture}
          </div>
        )}
        <div className='flex flex-col items-center gap-xs'>
          <h2 className='text-xl font-semibold'>{title}</h2>
          {message && <p className='text-sm text-foreground-muted max-w-md'>{message}</p>}
        </div>
        {action && <div>{action}</div>}
      </div>
    );
  }
);

HeroSection.displayName = 'HeroSection';
